export type CompanyPlacesSearchScope = 'country' | 'service_area';
export type PlacesAutocompleteMode = 'area' | 'address' | 'establishment';

export const DEFAULT_COMPANY_PLACES_SEARCH_SCOPE: CompanyPlacesSearchScope = 'country';

export function parseCompanyPlacesSearchScope(
  raw: unknown,
  fallback: CompanyPlacesSearchScope = DEFAULT_COMPANY_PLACES_SEARCH_SCOPE,
): CompanyPlacesSearchScope {
  const value = String(raw ?? '')
    .trim()
    .toLowerCase();
  if (value === 'country' || value === 'service_area') return value;
  return fallback;
}

export function resolveCompanyPlacesSearchScope(input: {
  orgSettings?: Record<string, unknown> | null;
  platformScope?: unknown;
}): CompanyPlacesSearchScope {
  const orgScope = input.orgSettings?.company_places_search_scope;
  if (orgScope === 'country' || orgScope === 'service_area') return orgScope;
  return parseCompanyPlacesSearchScope(input.platformScope);
}

/** Area/address stay near the office. Company search is country-wide unless Super Admin/org choose service area. */
export function shouldApplyPlacesLocationBias(
  mode: PlacesAutocompleteMode,
  companyScope: CompanyPlacesSearchScope,
): boolean {
  if (mode === 'establishment') return companyScope === 'service_area';
  return true;
}

export function clampPlacesBiasRadiusMeters(capKm: number): number {
  const km = Number.isFinite(capKm) && capKm > 0 ? capKm : 100;
  return Math.min(Math.max(km * 1000, 35000), 200000);
}

export function buildPlacesAutocompleteSearchParams(input: {
  query: string;
  key: string;
  mode: PlacesAutocompleteMode;
  countryIso?: string;
  bias?: { lat: number; lng: number } | null;
  capKm?: number;
  companyPlacesSearchScope?: CompanyPlacesSearchScope;
}): URLSearchParams {
  const params = new URLSearchParams();
  params.set('input', input.query);
  params.set('key', input.key);

  const countryIso = String(input.countryIso ?? '')
    .trim()
    .toLowerCase();
  if (countryIso.length === 2) {
    params.set('components', `country:${countryIso}`);
  }

  const scope = parseCompanyPlacesSearchScope(input.companyPlacesSearchScope);
  if (shouldApplyPlacesLocationBias(input.mode, scope) && input.bias) {
    params.set('location', `${input.bias.lat},${input.bias.lng}`);
    params.set('radius', String(clampPlacesBiasRadiusMeters(input.capKm ?? 100)));
    params.set('strictbounds', 'false');
  }

  if (input.mode === 'address') {
    params.set('types', 'address');
  } else if (input.mode === 'establishment') {
    params.set('types', 'establishment');
  }

  return params;
}
